import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AgentAdminService } from '../../../_services/api/agent-admin.service';
import { AgentDetail, AgentCreate, AgentUpdate } from '../../../_models/agent.model';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { Address } from '../../../_models/address.model';

@Component({
  selector: 'app-admin-agent-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './admin-agent-form.html',
  styleUrls: ['./admin-agent-form.scss']
})
export class AdminAgentForm implements OnInit, OnDestroy {
  agentForm: FormGroup;
  addressForm: FormGroup;
  isEditMode = false;
  agentId?: number;
  isLoading = false;
  isSubmitting = false;
  showSuccess = false;
  successMessage = '';
  emailAlreadyExists = false;
  
  // For file upload (profile image)
  selectedFile: File | null = null;
  previewUrl: string | null = null;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private agentService: AgentAdminService,
    private route: ActivatedRoute,
    private router: Router,
    private notification: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.addressForm = this.fb.group({
      streetNumber: ['', [Validators.required, Validators.maxLength(200)]],
      street: ['', Validators.maxLength(200)],
      city: ['', [Validators.required, Validators.maxLength(100)]],
      postalCode: ['', [Validators.required, Validators.pattern(/^[0-9]{4,10}$/)]],
    });

    this.agentForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^[\d\s\+\-\(\)]{8,20}$/)]],
      birthDate: [''],
      weight: ['', [Validators.min(20), Validators.max(300)]],
      height: ['', [Validators.min(100), Validators.max(250)]],
      specialization: ['', [Validators.required, Validators.maxLength(100)]],
      yearsOfExperience: ['', [Validators.required, Validators.min(0), Validators.max(50)]],
      bio: ['', Validators.maxLength(500)],
      hourlyRate: ['', [Validators.min(0), Validators.max(1000)]],
      isAvailable: [true],
      address: this.addressForm
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.agentId = +params['id'];
        this.loadAgentDetails();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAgentDetails(): void {
    if (!this.agentId) return;
    
    this.isLoading = true;
    this.agentService.getAgentDetails(this.agentId).subscribe({
      next: (response: ApiResponse<AgentDetail>) => {
        const agent = response.data;
        if (agent.address) {
          this.addressForm.patchValue(agent.address);
        }
        this.agentForm.patchValue({
          firstName: agent.firstName,
          lastName: agent.lastName,
          email: agent.email,
          phoneNumber: agent.phoneNumber,
          birthDate: agent.birthDate ? this.formatDateForInput(agent.birthDate) : '',
          weight: agent.weight || '',
          height: agent.height || '',
          specialization: agent.specialization || '',
          yearsOfExperience: agent.yearsOfExperience || '',
          bio: agent.bio || '',
          hourlyRate: agent.hourlyRate || '',
          isAvailable: agent.isAvailable
        });
        if (agent.profileImage) {
          this.previewUrl = agent.profileImage;
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement de l\'agent:', error);
        this.notification.showError('Erreur lors du chargement de l\'agent');
        this.isLoading = false;
        this.router.navigate(['/admin/agents']);
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        this.notification.showError('Veuillez sélectionner une image valide (JPG, PNG, GIF)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        this.notification.showError('L\'image ne doit pas dépasser 5MB');
        return;
      }
      
      this.selectedFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(): void {
    this.selectedFile = null;
    this.previewUrl = null;
  }

  onSubmit(): void {
    this.emailAlreadyExists = false;
    this.markFormGroupTouched(this.agentForm);
    this.markFormGroupTouched(this.addressForm);
    
    if (this.agentForm.invalid || this.addressForm.invalid) {
      this.notification.showError('Veuillez corriger les erreurs dans le formulaire');
      this.scrollToFirstError();
      return;
    }

    this.isSubmitting = true;
    
    if (this.isEditMode && this.agentId) {
      this.updateAgent();
    } else {
      this.createAgent();
    }
  }

  private createAgent(): void {
    const formValue = this.agentForm.value;
    const address: Address = {
      streetNumber: formValue.address.streetNumber,
      street: formValue.address.street,
      city: formValue.address.city,
      postalCode: formValue.address.postalCode
    };
    const agentData: AgentCreate = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneNumber: formValue.phoneNumber,
      address: address,
      birthDate: formValue.birthDate || undefined,
      weight: formValue.weight ? Number(formValue.weight) : undefined,
      height: formValue.height ? Number(formValue.height) : undefined,
      isAvailable: formValue.isAvailable,
      specialization: formValue.specialization,
      yearsOfExperience: Number(formValue.yearsOfExperience),
      bio: formValue.bio || undefined,
      hourlyRate: formValue.hourlyRate ? Number(formValue.hourlyRate) : undefined
    };

    this.agentService.createAgent(agentData)
      .subscribe({
        next: (response: ApiResponse<AgentDetail>) => {
          this.showSuccess = true;
          //this.successMessage = 'Agent créé avec succès! Un email a été envoyé avec les instructions de connexion.';
          this.successMessage = 'Agent créé avec succès!';
          this.notification.showSuccess('Agent créé avec succès');
          this.resetForm();
          
          setTimeout(() => {
            this.isSubmitting = false;
            if (isPlatformBrowser(this.platformId)) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 500);
        },
        error: (error) => {
          console.error('Erreur lors de la création de l\'agent:', error);
          this.isSubmitting = false;
          
          if (error.status === 409) {
            if (error.error?.message?.includes('Email')) {
              this.emailAlreadyExists = true;
              this.notification.showError('Cet email est déjà utilisé');
            }
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la création de l\'agent');
          }
        }
      });
  }

  private updateAgent(): void {
    if (!this.agentId) return;
    const formValue = this.agentForm.value;
    const address: Address | undefined = formValue.address.addressLine1 ? {
      streetNumber: formValue.address.streetNumber,
      street: formValue.address.street,
      city: formValue.address.city,
      postalCode: formValue.address.postalCode
    } : undefined;

    const agentData: AgentUpdate = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phoneNumber,
      address: address,
      birthDate: formValue.birthDate || undefined,
      weight: formValue.weight ? Number(formValue.weight) : undefined,
      height: formValue.height ? Number(formValue.height) : undefined,
      isAvailable: formValue.isAvailable,
      specialization: formValue.specialization,
      yearsOfExperience: Number(formValue.yearsOfExperience),
      bio: formValue.bio || undefined,
      hourlyRate: formValue.hourlyRate ? Number(formValue.hourlyRate) : undefined
    };

    Object.keys(agentData).forEach(key => {
      if (agentData[key as keyof AgentUpdate] === undefined) {
        delete agentData[key as keyof AgentUpdate];
      }
    });

    this.agentService.updateAgent(this.agentId, agentData)
      .subscribe({
        next: (response: ApiResponse<AgentDetail>) => {          
          this.notification.showSuccess('Agent modifié avec succès');
          this.router.navigate(['/admin/agents/details', this.agentId]);
        },
        error: (error) => {
          console.error('Erreur lors de la modification de l\'agent:', error);
          this.isSubmitting = false;
          
          if (error.status === 409) {
            this.emailAlreadyExists = true;
            this.notification.showError('Cet email est déjà utilisé par un autre agent');
            this.scrollToInput();
          } else {
            this.notification.showError('Erreur lors de la modification de l\'agent');
          }
        }
      });
  }

  private resetForm(): void {
    this.agentForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      birthDate: '',
      weight: '',
      height: '',
      specialization: '',
      yearsOfExperience: '',
      bio: '',
      hourlyRate: '',
      isAvailable: true
    });
    
    this.addressForm.reset({
      streetNumber: '',
      street: '',
      city: '',
      postalCode: '',
    });
    
    this.selectedFile = null;
    this.previewUrl = null;
    this.agentForm.markAsPristine();
    this.agentForm.markAsUntouched();
    this.addressForm.markAsPristine();
    this.addressForm.markAsUntouched();
  }

  createAnother(): void {
    this.showSuccess = false;
    this.resetForm();
  }

  goToList(): void {
    this.router.navigate(['/admin/agents']);
  }

  @ViewChild('firstError') firstErrorElement!: ElementRef;
  scrollToFirstError() {
    if (this.firstErrorElement) {
      this.firstErrorElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        this.firstErrorElement.nativeElement.focus();
      }, 200);
    }
  }

  scrollToInput() {
    if (this.emailAlreadyExists) {
      if (isPlatformBrowser(this.platformId)) {
        const emailInput = document.getElementById('email');
        emailInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailInput?.focus();
      }      
    }
  }

  private formatDateForInput(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get firstName() { return this.agentForm.get('firstName'); }
  get lastName() { return this.agentForm.get('lastName'); }
  get email() { return this.agentForm.get('email'); }
  get phoneNumber() { return this.agentForm.get('phoneNumber'); }
  get birthDate() { return this.agentForm.get('birthDate'); }
  get weight() { return this.agentForm.get('weight'); }
  get height() { return this.agentForm.get('height'); }
  get specialization() { return this.agentForm.get('specialization'); }
  get yearsOfExperience() { return this.agentForm.get('yearsOfExperience'); }
  get bio() { return this.agentForm.get('bio'); }
  get hourlyRate() { return this.agentForm.get('hourlyRate'); }
  get isAvailable() { return this.agentForm.get('isAvailable'); }

  get streetNumber() { return this.addressForm.get('streetNumber'); }
  get street() { return this.addressForm.get('street'); }
  get city() { return this.addressForm.get('city'); }
  get postalCode() { return this.addressForm.get('postalCode'); }
}
